import { useCallback, useEffect, useState } from 'react';
import { Form, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getSpouseTitle } from './constants';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { MILITARY_SERVICE_OPTIONS } from '../sponsor/FamilyApplicationStart';

export function FamilyAppSpouseEmploymentType({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;
  const spouseTitle = getSpouseTitle(application);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const _spouse = application?.additionalParents?.[0];
        const _isFullTimeEmployee = values.employmentType.includes('fullTimeEmployee');
        const _isSelfEmployed = values.employmentType.includes('selfEmployed');
        const _isMilitaryEmployee = values.employmentType.includes('militaryEmployee');

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            fullTimeEmployee: _isFullTimeEmployee,
            // FAMILY-APPS__DATA_CLEANING => SPOUSE_EMPLOYER_DETAILS step
            employmentDetails: _isFullTimeEmployee ? _spouse?.employmentDetails : null,
            selfEmployed: _isSelfEmployed,
            // FAMILY-APPS__DATA_CLEANING => SPOUSE_SELF_EMPLOYMENT step
            selfEmploymentDetails: _isSelfEmployed ? _spouse?.selfEmploymentDetails : null,
            militaryEmployee: _isMilitaryEmployee,
            // FAMILY-APPS__DATA_CLEANING => SPOUSE_MILITARY_EMPLOYMENT step
            militaryDetails: _isMilitaryEmployee ? _spouse?.militaryDetails : null,
          },
        );

        const _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic?.noticeError?.(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial values
  useEffect(() => {
    if (application?.additionalParents?.[0]) {
      const spouse = application.additionalParents[0];
      const isDualMilitary = application?.serviceMember === MILITARY_SERVICE_OPTIONS.BOTH;

      // employmentType is an array of strings
      const employmentType = [];
      if (spouse.fullTimeEmployee) {
        employmentType.push('fullTimeEmployee');
      }
      if (spouse.selfEmployed) {
        employmentType.push('selfEmployed');
      }
      if (spouse.militaryEmployee) {
        employmentType.push('militaryEmployee');
      }

      // If no employment type has been selected yet and serviceMember is dual military, default to militaryEmployee
      if (employmentType.length === 0 && isDualMilitary) {
        employmentType.push('militaryEmployee');
      }

      form.setFieldsValue({ employmentType });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title={`${spouseTitle.charAt(0).toUpperCase() + spouseTitle.slice(1)} Employment Type`} />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `What type of work does ${getOtherAdultFirstName(application?.additionalParents?.[0])} do?`
                : 'What type of work do you do?'
            }
            guidance="Select all that apply"
            className="my-12"
          />

          <Form.Item name="employmentType" rules={[{ required: true, message: 'Please select an employment type' }]}>
            <RadioCards
              multiple
              options={[
                {
                  label: 'Salaried (or hourly) employment',
                  value: 'fullTimeEmployee',
                },
                {
                  label: 'Self-employed or contractor',
                  value: 'selfEmployed',
                },
                {
                  label: 'Also in the military (dual military family)',
                  value: 'militaryEmployee',
                },
              ]}
            />
          </Form.Item>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_EMPLOYMENT_TYPE,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

export default FamilyAppSpouseEmploymentType;
